/* eslint-disable react/prop-types */
import { getStorageValue } from '@portal-internet/core';
import clsx from 'clsx';
import Icona from 'components/3cat/ui/Icona/Icona';
import { hitAudiencies } from 'components/3cat/Audiencies/audiencies-datalayer';
import { useLaMevaLlista } from 'hooks/3cat/useLaMevaLlista';
import { useProducte } from 'hooks/3cat/useProducte';
import { useUser } from 'hooks/useUser';
import useIsServiceEnabled from 'hooks/useIsServiceEnabled';
import __isEmpty from 'lodash/isEmpty';
import __isUndefined from 'lodash/isUndefined';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import styles from './afegeixalallista.module.scss';

const ADD_TO_LIST_NAMESPACE = '[web]items-usuari-afegeix-a-la-llista';
const ADD_TO_LIST_QUERY_KEYS = {
    legacyNamespace: 'namespace',
    legacyItemId: 'addItemToLlistaUsuari',
    action: 'act',
    actionItemId: 'i'
};
const ADD_TO_LIST_QUERY_ACTIONS = {
    addToList: 'ml'
};
const TOOLTIP_TEXTS = {
    added: 'Afegit a "La meva llista"',
    removed: 'Eliminat de "La meva llista"',
    addError: `No s'ha pogut afegir a "La meva llista"`,
    removeError: `No s'ha pogut eliminar de "La meva llista"`,
    addAction: 'Afegeix a "La meva llista"',
    removeAction: 'Elimina de "La meva llista"'
};

const getCurrentPageItemId = (asPath) => {
    const [pathname = ''] = asPath?.split('?') || [];
    const pathnameParts = pathname.split('/').filter(Boolean);
    const lastPathSegment = pathnameParts[pathnameParts.length - 1];

    return /^\d+$/.test(lastPathSegment) ? lastPathSegment : null;
};

const getPendingAddToListAction = (asPath) => {
    if (!asPath?.includes('?')) {
        return null;
    }

    const [, queryString = ''] = asPath.split('?');
    const params = new URLSearchParams(queryString);
    const action = params.get(ADD_TO_LIST_QUERY_KEYS.action);
    const shortItemId = params.get(ADD_TO_LIST_QUERY_KEYS.actionItemId);
    const currentPageItemId = getCurrentPageItemId(asPath);

    // Nou format curt d'URL que només fa de pont cap al flux existent d'afegir a la llista.
    // Si la pàgina de detall ja porta l'id a la ruta, el prioritzem per no afectar altres instàncies del component.
    if (action === ADD_TO_LIST_QUERY_ACTIONS.addToList && (currentPageItemId || !__isUndefined(shortItemId))) {
        return {
            namespace: ADD_TO_LIST_NAMESPACE,
            addItemToLlistaUsuari: currentPageItemId || shortItemId,
            isShortUrlAction: true
        };
    }

    if (action === ADD_TO_LIST_QUERY_ACTIONS.addToList) {
        return {
            namespace: ADD_TO_LIST_NAMESPACE,
            isShortUrlAction: true
        };
    }

    const namespace = params.get(ADD_TO_LIST_QUERY_KEYS.legacyNamespace);
    const addItemToLlistaUsuari = params.get(ADD_TO_LIST_QUERY_KEYS.legacyItemId);

    if (!namespace || __isUndefined(addItemToLlistaUsuari)) {
        return null;
    }

    return {
        namespace,
        addItemToLlistaUsuari
    };
};

function AfegeixALaLlista(props) {
    const { tooltipPosition, className, id, tipologia, positionCarrussel, audiencies } = props;
    const router = useRouter();
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [tooltipText, setTooltipText] = useState('');
    const [isTouched, setIsTouched] = useState(false);
    const [isBackFromLogin, setIsBackFromLogin] = useState(false);
    const [positionInFetch, setPositionInFetch] = useState(null);
    const [hasResolvedPositionInFetch, setHasResolvedPositionInFetch] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { isALaMevaLlista, eliminaALaMevaLlista, afegeixALaMevaLlista, loaded } = useLaMevaLlista();
    const isItemAlaMevaLlista = isALaMevaLlista(id, tipologia);
    const { isAuthenticated, accessToken, state: userState, signUp, loginState, loginStateTypes } = useUser();
    const isVrEnabled = useIsServiceEnabled('videos-recomanats');
    const isRmEnabled = useIsServiceEnabled('resume-media');
    const isSpmEnabled = isVrEnabled && isRmEnabled;
    const timeoutRef = useRef();
    const ref = useRef();
    const urlActionStatusRef = useRef('idle');
    const hasConsumedUserStateAction = useRef(false);
    const pendingAddToListAction = getPendingAddToListAction(router.asPath);
    const isPendingUrlActionForCurrentItem =
        pendingAddToListAction?.namespace === ADD_TO_LIST_NAMESPACE &&
        String(pendingAddToListAction?.addItemToLlistaUsuari) === String(id);

    const { isWebviewActive } = useProducte();
    const stylesAfegeix = clsx({
        [styles.boto]: true,
        [styles.hover]: isTooltipVisible,
        [styles.buttonShow]: isBackFromLogin,
        [className]: !__isEmpty(className)
    });
    const stylesTooltip = clsx({
        [styles.tooltip]: true,
        [styles.tooltipfloat]: !isTouched,
        [styles.tooltiptouch]: isTouched,
        [styles[tooltipPosition]]: true
    });

    const waitAndHideTooltip = () => {
        clearTimeout(timeoutRef?.current);
        timeoutRef.current = setTimeout(() => {
            setIsTooltipVisible(false);
        }, 3000);
    };

    const showBackFromLoginState = () => {
        const timeoutScroll = setTimeout(() => {
            if (__isUndefined(userState?.positionCarrussel)) {
                ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);

        setTooltipText(TOOLTIP_TEXTS.added);
        setIsTooltipVisible(true);
        waitAndHideTooltip();
        setIsBackFromLogin(true);
        const timeoutTooltip = setTimeout(() => {
            setIsBackFromLogin(false);
        }, 4000);

        return () => {
            clearTimeout(timeoutScroll);
            clearTimeout(timeoutTooltip);
        };
    };

    const onActionToList = async () => {
        // Flux normal: clic al botó. Si l'usuari no està logat, guardem la intenció i iniciem el login.
        if (!isAuthenticated) {
            signUp({
                namespace: ADD_TO_LIST_NAMESPACE,
                addItemToLlistaUsuari: id,
                currentPage: positionInFetch,
                positionCarrussel: positionCarrussel
            });
            return;
        }

        setIsLoading(true);
        const statusIsOk = isItemAlaMevaLlista
            ? await eliminaALaMevaLlista(id, tipologia)
            : await afegeixALaMevaLlista(id, tipologia);
        setIsLoading(false);

        if (statusIsOk) {
            setTooltipText(isItemAlaMevaLlista ? TOOLTIP_TEXTS.removed : TOOLTIP_TEXTS.added);
            if (!isItemAlaMevaLlista) {
                hitAudiencies('scPerMesTard', { audiencies });
            }
        } else {
            setTooltipText(isItemAlaMevaLlista ? TOOLTIP_TEXTS.removeError : TOOLTIP_TEXTS.addError);
        }

        setIsTooltipVisible(true);
        waitAndHideTooltip();
    };

    useEffect(() => {
        return () => clearTimeout(timeoutRef?.current);
    }, []);

    useEffect(() => {
        let timeout = null;

        if (isWebviewActive) {
            setHasResolvedPositionInFetch(true);
            return;
        }

        setHasResolvedPositionInFetch(false);
        timeout = setTimeout(async () => {
            setPositionInFetch(await getStorageValue('web-fetch-page'));
            setHasResolvedPositionInFetch(true);
        }, 100);

        return () => {
            if (timeout) {
                clearTimeout(timeout);
            }
        };
    }, [isWebviewActive]);

    useEffect(() => {
        // Si ja no venim d'una URL amb acció pendent per a aquest ítem, o si l'usuari ha canviat d'intenció al userState, reiniciem els flags del flux userState.
        if (
            !isAuthenticated ||
            userState?.namespace !== ADD_TO_LIST_NAMESPACE ||
            String(userState?.addItemToLlistaUsuari) !== String(id)
        ) {
            hasConsumedUserStateAction.current = false;
        }
    }, [id, isAuthenticated, userState]);

    // Flux URL complet: si cal, inicia login; quan l'usuari torna logat, reprèn l'acció i neteja la query string.
    useEffect(() => {
        if (!isPendingUrlActionForCurrentItem) {
            urlActionStatusRef.current = 'idle';
            return;
        }

        if (!isSpmEnabled) {
            return;
        }

        if (!isWebviewActive && !hasResolvedPositionInFetch) {
            return;
        }

        if (isAuthenticated && !loaded) {
            return;
        }

        if (!isAuthenticated) {
            if (urlActionStatusRef.current === 'idle') {
                urlActionStatusRef.current = 'login-triggered';
                void onActionToList();
            }
            return;
        }

        if (loginState === loginStateTypes.INIT) {
            return;
        }

        if (!accessToken || urlActionStatusRef.current === 'completed') {
            return;
        }

        urlActionStatusRef.current = 'completed';

        const cleanupBackFromLoginState = showBackFromLoginState();

        const runPendingUrlAction = async () => {
            if (!isItemAlaMevaLlista) {
                await onActionToList();
            }
        };

        void runPendingUrlAction();

        return () => {
            cleanupBackFromLoginState?.();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, hasResolvedPositionInFetch, isAuthenticated, isItemAlaMevaLlista, isPendingUrlActionForCurrentItem, isSpmEnabled, isWebviewActive, loaded, loginState, loginStateTypes.INIT]);

    useEffect(() => {
        // Retorn general després del login: reprenem una intenció guardada al userState, vingui del botó o d'un altre flux.
        if (
            !isSpmEnabled ||
            !isAuthenticated ||
            !accessToken ||
            !loaded ||
            isPendingUrlActionForCurrentItem ||
            hasConsumedUserStateAction.current ||
            userState?.namespace !== ADD_TO_LIST_NAMESPACE ||
            String(userState?.addItemToLlistaUsuari) !== String(id)
        ) {
            return;
        }

        hasConsumedUserStateAction.current = true;

        if (!isItemAlaMevaLlista) {
            void onActionToList();
        }

        return showBackFromLoginState();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accessToken, id, isAuthenticated, isItemAlaMevaLlista, isPendingUrlActionForCurrentItem, loaded, isSpmEnabled, userState]);

    const onClickList = () => {
        if (!isLoading) {
            void onActionToList();
        }
    };

    const iconButton = () => {
        if (isLoading) {
            return <div className={styles.loadingIcon} />;
        }
        const estilIcona = isItemAlaMevaLlista ? 'checked_contorn_cercle' : 'mes_contorn_cercle';
        return <Icona inline={true} icona={estilIcona} color='blanc' alt='' />;
    };

    const altIcona = isItemAlaMevaLlista ? TOOLTIP_TEXTS.removeAction : TOOLTIP_TEXTS.addAction;
    return isSpmEnabled ? (
        <button
            ref={ref}
            className={stylesAfegeix}
            aria-label={altIcona}
            onTouchStart={() => {
                setIsTouched(true);
            }}
            onClick={onClickList}
            onMouseEnter={() => {
                setTooltipText(isItemAlaMevaLlista ? TOOLTIP_TEXTS.removeAction : TOOLTIP_TEXTS.addAction);
                setIsTooltipVisible(true);
            }}
            onMouseLeave={() => {
                clearTimeout(timeoutRef?.current);
                setIsTooltipVisible(false);
            }}
            data-testid='afegeix-a-la-llista'
        >
            {iconButton()}
            {isTooltipVisible && (
                <div className={stylesTooltip} aria-hidden='true'>
                    {tooltipText}
                </div>
            )}
        </button>
    ) : null;
}

AfegeixALaLlista.defaultProps = {
    tooltipPosition: 'left'
};

export default AfegeixALaLlista;
